import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CampaignElementsList, MetaCampaignElementsList } from '@/components/features/CampaignStructure';
import { CreateAdSetWizard } from '@/components/features/MetaAdCreation/CreateAdSetWizard';
import { CreateMetaAdWizard } from '@/components/features/MetaAdCreation/CreateMetaAdWizard';
import { CreativeLibraryPicker } from '@/components/features/MetaAdCreation/CreativeLibraryPicker';
import { Button } from '@/components/ui/Button';
import { supportsAdGroups } from '@/types/adGroup';
import type { Campaign } from '@/types/campaign';
import { isMetaCampaign } from '@/types/campaign';
import type { RDACacheEntry } from '@/hooks/useRDACache';
import type { DemandGenCacheEntry } from '@/hooks/useDemandGenCache';
import type {
  MetaAdCreative,
  MetaCreativeSource,
} from '@/services/api/metaAds';
import type { StructureSummary } from '../hooks/useCampaignData';
import type { WizardsState } from '../hooks/useWizards';

interface CampaignStructureProps {
  campaign: Campaign;
  campaignId: string;
  structureSummary: StructureSummary;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onEntityRemoved: (entityId: string) => void;
  wizards: WizardsState;
}

/**
 * Campaign structure summary showing ad groups/asset groups and entity lists.
 */
export function CampaignStructure({
  campaign,
  campaignId,
  structureSummary,
  rdaCache,
  isLoadingRDAs,
  demandGenCache,
  isLoadingDemandGenAds,
  onEntityRemoved,
  wizards,
}: CampaignStructureProps) {
  const queryClient = useQueryClient();
  // A4.3.3 — CreativeLibraryPicker is the entry point for the ad wizard.
  // Picker state lives here (not in `useWizards`) because the picker
  // hands a `creativeSource` to the wizard at the moment of open; the
  // wizard mounts conditionally with that source as a prop.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedSource, setPickedSource] = useState<MetaCreativeSource | null>(
    null,
  );
  const [pickedCreative, setPickedCreative] = useState<MetaAdCreative | null>(
    null,
  );

  const handleElementUpdated = async () => {
    // Meta create wizards (CreateMetaCampaignWizard / CreateAdSetWizard /
    // CreateMetaAdWizard) already splice the new entity into ['app-config']
    // via setQueryData before this fires, so an app-config refetch here
    // would clobber the optimistic write with a backend response that may
    // not yet reflect the just-written entity. See
    // docs/FRONTEND_MUTATION_PATTERNS.md.
    //
    // For Google create paths (CreateAdGroupWizard, CreateRSAWizard etc.)
    // the same rule holds — they write optimistically and rely on
    // campaign-from-cache re-derive.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
    // Note: RDA cache auto-updates because its query key includes rdaIdsKey
    // which changes when the campaign's RDA list changes.
  };

  const isPerformanceMax =
    structureSummary.assetGroupCount > 0 ||
    campaign.campaign_type?.includes('PERFORMANCE_MAX');
  const isMeta = isMetaCampaign(campaign);

  // Meta short-circuit: ad-sets + ads counts, the read-only summary list,
  // and disabled scaffolds for create wizards (real handlers land in A4
  // per META_INTEGRATION_PLAN.md §9.3, gated on the §9.0.1 substrate
  // decision). Returns early so we don't fall through to the Google
  // PMax/Search branches.
  if (isMeta) {
    const adSets = campaign.ad_sets ?? [];
    // Active Ads = ads whose effective_status (Meta's authoritative
    // surfaced state) is ACTIVE. Falls back to `status` for synthetic
    // rows that lack the effective field. This is the third top-level
    // stat that pairs visually with Google's "Total Assets" slot.
    const activeAds = adSets.reduce((sum, as) => {
      return sum + (as.ads?.filter((a) => {
        const s = (a.effective_status ?? a.status ?? '').toUpperCase();
        return s === 'ACTIVE';
      }).length ?? 0);
    }, 0);
    const metaAdAccountId =
      (campaign as Campaign & { meta_ad_account_id?: string })
        .meta_ad_account_id ?? '';
    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Campaign Structure</h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <p className="text-sm text-gray-500">Ad Sets</p>
            <p className="text-2xl font-bold text-gray-900">{structureSummary.adGroupCount}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Ads</p>
            <p className="text-2xl font-bold text-gray-900">{structureSummary.totalAds}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Active Ads</p>
            <p className="text-2xl font-bold text-gray-900">{activeAds}</p>
          </div>
        </div>
        {/* "Ad Sets & Ads" tree — Meta analog of Google's
            "Ad Groups & Ads" tree from `CampaignElementsList`. The status
            badge + kebab actions for every ad set and ad live in this
            tree (mirroring Google); the Manage Campaign accordion
            below no longer surfaces those controls. */}
        <MetaCampaignElementsList
          campaign={campaign}
          onElementUpdated={handleElementUpdated}
          onEntityArchived={onEntityRemoved}
        />
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <Button
            variant="success"
            onClick={wizards.metaAdSetWizard.open}
            className="w-full"
          >
            + Create New Ad Set
          </Button>
          {(() => {
            // Two gating conditions, in priority order. Compute up
            // front so the disabled state + title match.
            const noAdSets = (campaign.ad_sets ?? []).length === 0;
            const noAdAccount = !metaAdAccountId;
            const disabledReason = noAdAccount
              ? 'This campaign is missing its Meta ad account ID — refresh ' +
                'and reconnect the Meta account if this persists.'
              : noAdSets
                ? 'Create an ad set first.'
                : undefined;
            return (
              <Button
                variant="success"
                onClick={() => setPickerOpen(true)}
                disabled={noAdSets || noAdAccount}
                title={disabledReason}
                className="w-full"
              >
                + Create New Ad
              </Button>
            );
          })()}
        </div>
        {wizards.metaAdSetWizard.isOpen && (
          <CreateAdSetWizard
            isOpen
            onClose={wizards.metaAdSetWizard.close}
            onSuccess={handleElementUpdated}
            campaignId={campaignId}
            parentCampaign={campaign}
          />
        )}
        <CreativeLibraryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          metaAdAccountId={metaAdAccountId}
          onPick={(source, creative) => {
            setPickedSource(source);
            setPickedCreative(creative);
            setPickerOpen(false);
            wizards.metaAdWizard.open();
          }}
        />
        {wizards.metaAdWizard.isOpen && pickedSource && (
          <CreateMetaAdWizard
            isOpen
            onClose={() => {
              wizards.metaAdWizard.close();
              setPickedSource(null);
              setPickedCreative(null);
            }}
            onSuccess={handleElementUpdated}
            campaignId={campaignId}
            parentCampaign={campaign}
            creativeSource={pickedSource}
            pickedCreative={pickedCreative}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow border">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Campaign Structure</h2>

      {isPerformanceMax ? (
        /* Performance Max Structure */
        <>
          <div className="flex justify-center gap-32">
            <div className="text-center">
              <p className="text-sm text-gray-500">Asset Groups</p>
              <p className="text-2xl font-bold text-gray-900">{structureSummary.assetGroupCount}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Assets</p>
              <p className="text-2xl font-bold text-gray-900">{structureSummary.totalAssets}</p>
            </div>
          </div>
          {/* Asset Groups List with Actions */}
          <CampaignElementsList
            campaign={campaign}
            isPerformanceMax={true}
            rdaCache={rdaCache}
            isLoadingRDAs={isLoadingRDAs}
            demandGenCache={demandGenCache}
            isLoadingDemandGenAds={isLoadingDemandGenAds}
            onEntityRemoved={onEntityRemoved}
            onElementUpdated={handleElementUpdated}
          />
          {/* Add Asset Group Button - Only for Performance Max campaigns */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              variant="success"
              onClick={wizards.assetGroupWizard.open}
              className="w-full"
            >
              + Create New Asset Group
            </Button>
          </div>
        </>
      ) : (
        /* Search/Display/AI MAX Structure */
        <>
          {structureSummary.adGroupCount === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No ad groups yet</p>
              <p className="text-sm text-gray-400">Create your first ad group to start running ads</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Ad Groups</p>
                  <p className="text-2xl font-bold text-gray-900">{structureSummary.adGroupCount}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Ads</p>
                  <p className="text-2xl font-bold text-gray-900">{structureSummary.totalAds}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Assets</p>
                  <p className="text-2xl font-bold text-gray-900">{structureSummary.totalAssets}</p>
                </div>
              </div>
              {/* Ad Groups List with Actions */}
              <CampaignElementsList
                campaign={campaign}
                isPerformanceMax={false}
                rdaCache={rdaCache}
                isLoadingRDAs={isLoadingRDAs}
                demandGenCache={demandGenCache}
                isLoadingDemandGenAds={isLoadingDemandGenAds}
                onEntityRemoved={onEntityRemoved}
                onElementUpdated={handleElementUpdated}
              />
            </>
          )}
        </>
      )}

      {/* Add Ad Group Button - Only for campaigns that support ad groups */}
      {!structureSummary.assetGroupCount && supportsAdGroups(campaign.campaign_type) && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <Button
            variant="success"
            onClick={wizards.adGroupWizard.open}
            className="w-full"
          >
            + Create New Ad Group
          </Button>
          {/* Add RDA Button - Only for Display campaigns with existing ad groups */}
          {campaign.campaign_type?.includes('DISPLAY') &&
           campaign.ads_group_list &&
           campaign.ads_group_list.length > 0 && (
            <Button
              variant="success"
              onClick={wizards.rdaWizard.open}
              className="w-full"
            >
              + Create New Responsive Display Ad
            </Button>
          )}
          {/* Add RSA Button - Only for Search and AI_MAX campaigns with existing ad groups */}
          {(campaign.campaign_type?.includes('SEARCH') ||
            campaign.campaign_type?.includes('AI_MAX')) &&
           campaign.ads_group_list &&
           campaign.ads_group_list.length > 0 && (
            <Button
              variant="success"
              onClick={wizards.rsaWizard.open}
              className="w-full"
            >
              + Create New Responsive Search Ad
            </Button>
          )}
          {/* Add Demand Gen Ad Button - Only for Demand Gen campaigns with existing ad groups */}
          {campaign.campaign_type?.includes('DEMAND_GEN') &&
           campaign.ads_group_list &&
           campaign.ads_group_list.length > 0 && (
            <Button
              variant="success"
              onClick={wizards.demandGenAdWizard.open}
              className="w-full"
            >
              + Create New Demand Gen Ad
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
